import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './Clientes.module.css';
import ClienteModal from './ClienteModal';
import {
  listar,
  criar,
  atualizar,
  COLECOES,
} from '../../../services/firestore';

// ====================================================================
// CADASTRO DE CLIENTES
// Conectado ao Firestore — colecao: clientes  (Bloco 3.2)
// Estrutura/visual identicos a versao aprovada; muda so a fonte de dados.
// Blindagem: endereco/contato podem nao existir em registros do banco.
// ====================================================================

const ENDERECO_VAZIO = {
  logradouro: '',
  numero: '',
  bairro: '',
  cidade: '',
  uf: '',
  cep: '',
};
const CONTATO_VAZIO = {
  nome: '',
  cargo: '',
  telefone: '',
  email: '',
};

// Garante que todo cliente tenha endereco/contato como objeto (nunca undefined)
function normalizarCliente(c) {
  return {
    ...c,
    razaoSocial: c.razaoSocial || '',
    nomeFantasia: c.nomeFantasia || '',
    cnpj: c.cnpj || '',
    inscricaoEstadual: c.inscricaoEstadual || '',
    endereco: { ...ENDERECO_VAZIO, ...(c.endereco || {}) },
    contato: { ...CONTATO_VAZIO, ...(c.contato || {}) },
    ativo: c.ativo ?? true,
  };
}

export default function Clientes() {
  const navigate = useNavigate();

  const [clientes, setClientes] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);

  const [busca, setBusca] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');

  const [modalAberto, setModalAberto] = useState(false);
  const [clienteEditando, setClienteEditando] = useState(null);

  // ------------------------------------------------------------------
  // Carregar clientes do Firestore (normalizando aninhados)
  // ------------------------------------------------------------------
  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      const dados = await listar(COLECOES.CLIENTES, { ordenarPor: 'razaoSocial' });
      setClientes(dados.map(normalizarCliente));
    } catch (e) {
      setErro(e.message || 'Erro ao carregar clientes.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const clientesFiltrados = useMemo(() => {
    return clientes.filter((c) => {
      if (busca) {
        const q = busca.toLowerCase();
        const match =
          c.razaoSocial.toLowerCase().includes(q) ||
          (c.nomeFantasia && c.nomeFantasia.toLowerCase().includes(q)) ||
          c.cnpj.includes(q);
        if (!match) return false;
      }
      if (filtroStatus === 'Ativos' && !c.ativo) return false;
      if (filtroStatus === 'Inativos' && c.ativo) return false;
      return true;
    });
  }, [clientes, busca, filtroStatus]);

  const totalAtivos = clientes.filter((c) => c.ativo).length;
  const totalInativos = clientes.length - totalAtivos;

  function abrirNovo() {
    setClienteEditando(null);
    setModalAberto(true);
  }

  function abrirEditar(cliente) {
    setClienteEditando(cliente);
    setModalAberto(true);
  }

  function fecharModal() {
    setModalAberto(false);
    setClienteEditando(null);
  }

  // ------------------------------------------------------------------
  // Salvar (criar ou atualizar) no Firestore
  // Normaliza para garantir endereco/contato como objetos no banco
  // ------------------------------------------------------------------
  async function salvar(dados) {
    setSalvando(true);
    setErro('');
    try {
      const limpo = {
        ...dados,
        endereco: { ...ENDERECO_VAZIO, ...(dados.endereco || {}) },
        contato: { ...CONTATO_VAZIO, ...(dados.contato || {}) },
      };
      delete limpo.id;
      if (clienteEditando) {
        await atualizar(COLECOES.CLIENTES, clienteEditando.id, limpo);
      } else {
        await criar(COLECOES.CLIENTES, { ...limpo, ativo: limpo.ativo ?? true });
      }
      fecharModal();
      await carregar();
    } catch (e) {
      setErro(e.message || 'Erro ao salvar o cliente.');
    } finally {
      setSalvando(false);
    }
  }

  // ------------------------------------------------------------------
  // Ativar / Inativar no Firestore (otimista com rollback)
  // ------------------------------------------------------------------
  async function toggleAtivo(id) {
    const alvo = clientes.find((c) => c.id === id);
    if (!alvo) return;
    setErro('');
    setClientes((prev) =>
      prev.map((c) => (c.id === id ? { ...c, ativo: !c.ativo } : c))
    );
    try {
      await atualizar(COLECOES.CLIENTES, id, { ativo: !alvo.ativo });
    } catch (e) {
      setErro(e.message || 'Erro ao alterar o status do cliente.');
      setClientes((prev) =>
        prev.map((c) => (c.id === id ? { ...c, ativo: alvo.ativo } : c))
      );
    }
  }

  return (
    <>
      <div className={styles.topbar}>
        <div className={styles.pageTitle}>Cadastro de Clientes</div>
        <button className={styles.btnVoltar} onClick={() => navigate('/cadastros')}>
          ← Voltar para Cadastros
        </button>
      </div>

      {erro && (
        <div
          style={{
            background: '#fdecea',
            color: '#b51013',
            border: '1px solid #f5c6cb',
            borderRadius: 8,
            padding: '10px 14px',
            margin: '0 0 14px',
            fontSize: 14,
          }}
        >
          {erro}
        </div>
      )}

      <div className={styles.toolbar}>
        <input
          className={styles.toolbarInput}
          placeholder="🔍 Buscar cliente, CNPJ..."
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />
        <select
          className={styles.toolbarSelect}
          value={filtroStatus}
          onChange={(e) => setFiltroStatus(e.target.value)}
        >
          <option value="">Todos os status</option>
          <option value="Ativos">Ativos</option>
          <option value="Inativos">Inativos</option>
        </select>
        <button className={styles.btnNovo} onClick={abrirNovo}>
          + Novo cliente
        </button>
      </div>

      <div className={styles.tabelaWrap}>
        <div className={styles.tabelaHead}>
          <span className={styles.tabelaCount}>
            {clientes.length} clientes cadastrados ({totalAtivos} ativos · {totalInativos}{' '}
            {totalInativos === 1 ? 'inativo' : 'inativos'})
          </span>
          <span className={styles.tabelaHint}>Clique em ✏️ para editar</span>
        </div>

        <div className={styles.colHeader}>
          <div>Razão Social</div>
          <div>CNPJ</div>
          <div>Contato técnico</div>
          <div></div>
        </div>

        {carregando ? (
          <div className={styles.semResultados}>Carregando clientes...</div>
        ) : clientesFiltrados.length === 0 ? (
          <div className={styles.semResultados}>
            {clientes.length === 0
              ? 'Nenhum cliente cadastrado ainda. Clique em "+ Novo cliente" para começar.'
              : 'Nenhum cliente encontrado com os filtros aplicados.'}
          </div>
        ) : (
          clientesFiltrados.map((cliente) => (
            <div
              key={cliente.id}
              className={`${styles.row} ${!cliente.ativo ? styles.inativo : ''}`}
            >
              <div>
                <div className={styles.cliRazao}>{cliente.razaoSocial}</div>
                {cliente.nomeFantasia && (
                  <div className={styles.cliFantasia}>{cliente.nomeFantasia}</div>
                )}
              </div>
              <div>
                <div className={styles.cliCnpj}>{cliente.cnpj}</div>
                {cliente.endereco.cidade && (
                  <div className={styles.cliCidade}>
                    {cliente.endereco.cidade}
                    {cliente.endereco.uf ? ` · ${cliente.endereco.uf}` : ''}
                  </div>
                )}
              </div>
              <div>
                {cliente.contato.nome ? (
                  <>
                    <div className={styles.cliContatoNome}>{cliente.contato.nome}</div>
                    {cliente.contato.cargo && (
                      <div className={styles.cliContatoCargo}>
                        {cliente.contato.cargo}
                      </div>
                    )}
                    {cliente.contato.telefone && (
                      <div className={styles.cliContatoTel}>
                        {cliente.contato.telefone}
                      </div>
                    )}
                  </>
                ) : (
                  <span style={{ color: '#bbb', fontSize: 12 }}>
                    Sem contato cadastrado
                  </span>
                )}
              </div>
              <div className={styles.acoes}>
                <span
                  className={`${styles.statusTag} ${cliente.ativo ? styles.stAtivo : styles.stInativo}`}
                >
                  {cliente.ativo ? 'Ativo' : 'Inativo'}
                </span>
                <button
                  className={`${styles.btnAcao} ${styles.editar}`}
                  title="Editar"
                  onClick={() => abrirEditar(cliente)}
                >
                  ✏️
                </button>
                <button
                  className={`${styles.btnAcao} ${styles.inativar}`}
                  title={cliente.ativo ? 'Inativar' : 'Reativar'}
                  onClick={() => toggleAtivo(cliente.id)}
                >
                  {cliente.ativo ? '🚫' : '✅'}
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {modalAberto && (
        <ClienteModal
          cliente={clienteEditando}
          onSalvar={salvar}
          onCancelar={fecharModal}
          salvando={salvando}
        />
      )}
    </>
  );
}